/* Auvyroo storefront interactions.
   Cart uses Shopify's AJAX Cart API + Section Rendering API; checkout always happens on Shopify. */
(() => {
  const queryAll = (selector, root = document) => [...root.querySelectorAll(selector)];
  const one = (selector, root = document) => root.querySelector(selector);
  const config = window.auvyroo || {};
  const rootPath = () => {
    const root = config.routes?.root || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents) => {
    const format = String(config.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = (Number(cents) || 0) / 100;
    const number = (amount, decimals, thousands, decimal) => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return number(value, 0, ',', '.');
        case 'amount_with_comma_separator': return number(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return number(value, 2, "'", '.');
        default: return number(value, 2, ',', '.');
      }
    });
  };

  const initializeHeader = () => {
    const header = one('[data-site-header]');
    if (!header || header.dataset.initialized) return;
    header.dataset.initialized = 'true';
    const toggle = one('[data-menu-toggle]', header);
    const menu = one('[data-mobile-menu]', header);
    const setOpen = (open) => {
      if (!toggle || !menu) return;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close navigation menu' : 'Open navigation menu');
      menu.hidden = !open;
      header.classList.toggle('is-open', open);
    };
    toggle?.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    queryAll('a', menu || header).forEach((link) => link.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setOpen(false); });
    const syncScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
    window.addEventListener('scroll', syncScroll, { passive: true });
    syncScroll();
  };

  /* ---------- Announcement slideshow ---------- */
  const initializeAnnouncement = (root = document) => {
    queryAll('[data-announcement]', root).forEach((bar) => {
      if (bar.dataset.initialized) return;
      bar.dataset.initialized = 'true';
      const slides = queryAll('[data-announcement-slide]', bar);
      if (slides.length < 2) return;
      let active = 0;
      let timer = null;
      const speed = Math.max(3000, Number(bar.dataset.speed) || 4000);
      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const on = i === active;
          slide.classList.toggle('is-active', on);
          slide.setAttribute('aria-hidden', String(!on));
          queryAll('a', slide).forEach((link) => link.setAttribute('tabindex', on ? '0' : '-1'));
        });
      };
      const stop = () => { if (timer) window.clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        if (bar.dataset.autoplay === 'false' || prefersReducedMotion()) return;
        timer = window.setInterval(() => show(active + 1), speed);
      };
      one('[data-announcement-prev]', bar)?.addEventListener('click', () => { show(active - 1); start(); });
      one('[data-announcement-next]', bar)?.addEventListener('click', () => { show(active + 1); start(); });
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      let touchX = null;
      bar.addEventListener('touchstart', (e) => { touchX = e.changedTouches[0]?.screenX ?? null; stop(); }, { passive: true });
      bar.addEventListener('touchend', (e) => {
        if (touchX !== null) {
          const diff = (e.changedTouches[0]?.screenX ?? touchX) - touchX;
          if (Math.abs(diff) > 35) show(active + (diff < 0 ? 1 : -1));
        }
        touchX = null;
        start();
      }, { passive: true });
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
      start();
    });
  };

  const initializeGalleries = (root = document) => {
    queryAll('.hero-media, .product-page__gallery', root).forEach((gallery) => {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized = 'true';
      const thumbs = queryAll('[data-gallery-thumb]', gallery);
      const image = one('[data-gallery-main]', gallery);
      if (!image || thumbs.length === 0) return;
      const caption = one('[data-gallery-caption]', gallery);
      const counter = one('[data-gallery-counter]', gallery);
      let active = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));

      const select = (position) => {
        active = (position + thumbs.length) % thumbs.length;
        const button = thumbs[active];
        image.src = button.dataset.src;
        image.alt = button.dataset.alt || image.alt;
        if (caption) caption.textContent = button.dataset.caption || '';
        if (counter) counter.textContent = `${active + 1} / ${thumbs.length}`;
        thumbs.forEach((thumb, index) => {
          thumb.classList.toggle('is-active', index === active);
          thumb.setAttribute('aria-pressed', String(index === active));
        });
        queryAll('.hero-media__dots i', gallery).forEach((dot, index) => dot.classList.toggle('is-active', index === active));
        const strip = button.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          strip.scrollTo({ left: button.offsetLeft - (strip.clientWidth - button.offsetWidth) / 2, behavior: 'smooth' });
        }
      };

      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => select(index)));
      one('[data-gallery-prev]', gallery)?.addEventListener('click', () => select(active - 1));
      one('[data-gallery-next]', gallery)?.addEventListener('click', () => select(active + 1));

      const stage = one('[data-gallery-stage]', gallery);
      let touchStartX = null;
      stage?.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.screenX ?? null;
      }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const diff = (event.changedTouches[0]?.screenX ?? touchStartX) - touchStartX;
        if (Math.abs(diff) > 45) select(active + (diff < 0 ? 1 : -1));
        touchStartX = null;
      }, { passive: true });
    });
  };

  /* ---------- Cart drawer (slides in from the left) ---------- */
  const Cart = (() => {
    const drawer = () => one('[data-cart-drawer]');
    let lastFocus = null;

    const updateCount = () => {
      const inner = one('[data-cart-drawer] [data-cart-count-value]');
      if (!inner) return;
      const count = Number(inner.dataset.cartCountValue) || 0;
      queryAll('[data-cart-count]').forEach((bubble) => {
        bubble.textContent = String(count);
        bubble.hidden = count === 0;
      });
      queryAll('[data-cart-open]').forEach((link) => link.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`));
    };

    const render = (html) => {
      const target = one('[data-cart-drawer] [data-cart-drawer-inner]');
      if (!target || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = one('[data-cart-drawer-inner]', parsed);
      if (fresh) target.innerHTML = fresh.innerHTML;
      updateCount();
    };

    const refresh = async () => {
      const response = await fetch(`${rootPath()}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not refresh cart');
      const data = await response.json();
      render(data['cart-drawer']);
    };

    const showError = (message) => {
      const box = one('[data-cart-drawer] [data-cart-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
    };

    const trapFocus = (event) => {
      const el = drawer();
      if (!el || !el.classList.contains('is-open') || event.key !== 'Tab') return;
      const focusables = queryAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', one('[data-cart-panel]', el))
        .filter((node) => node.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };

    const open = () => {
      const el = drawer();
      if (!el) return false;
      lastFocus = document.activeElement;
      el.removeAttribute('inert');
      el.setAttribute('aria-hidden', 'false');
      el.classList.add('is-open');
      document.documentElement.classList.add('has-cart-open');
      window.setTimeout(() => one('[data-cart-close].cart-drawer__close', el)?.focus({ preventScroll: true }) || one('[data-cart-panel]', el)?.focus({ preventScroll: true }), 60);
      return true;
    };

    const close = () => {
      const el = drawer();
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
      document.documentElement.classList.remove('has-cart-open');
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    };

    const addonVariantIds = () => new Set(
      queryAll('[data-addon-variant]').map((node) => node.dataset.addonVariant)
        .concat(queryAll('[data-addon-quick-add]').map((node) => node.dataset.variant))
    );

    // Add items, never duplicating a one-per-order add-on that is already in the cart.
    const add = async (items) => {
      const addonIds = addonVariantIds();
      let toAdd = items.filter((item) => item.id && item.quantity > 0);
      if (toAdd.some((item) => addonIds.has(String(item.id)))) {
        try {
          const current = await (await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } })).json();
          const inCart = new Set(current.items.map((line) => String(line.variant_id)));
          toAdd = toAdd.filter((item) => !(addonIds.has(String(item.id)) && inCart.has(String(item.id))));
        } catch { /* fall through and let Shopify handle it */ }
      }
      if (!toAdd.length) { open(); return; }
      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: toAdd.map((item) => ({ id: Number(item.id), quantity: item.quantity })), sections: 'cart-drawer', sections_url: window.location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data.description || data.message || 'This item could not be added. Please try again.';
        await refresh().catch(() => {});
        showError(typeof message === 'string' ? message : 'This item could not be added.');
        open();
        throw new Error(message);
      }
      if (data.sections && data.sections['cart-drawer']) render(data.sections['cart-drawer']);
      else await refresh();
      showError('');
      open();
    };

    const change = async (key, quantity) => {
      const response = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity), sections: 'cart-drawer', sections_url: window.location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.description || 'That change could not be saved.');
        return;
      }
      if (data.sections && data.sections['cart-drawer']) render(data.sections['cart-drawer']);
      else await refresh();
    };

    const setBusy = (button, busy, label = 'Adding…') => {
      if (!button) return;
      const text = one('[data-add-to-cart-label], [data-add-bundle-label]', button) || button;
      if (busy) {
        button.dataset.originalLabel = text.textContent;
        text.textContent = label;
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      } else {
        if (button.dataset.originalLabel) text.textContent = button.dataset.originalLabel;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    };

    const initialize = () => {
      const el = drawer();
      if (!el || el.dataset.initialized) return;
      el.dataset.initialized = 'true';
      document.addEventListener('click', async (event) => {
        const opener = event.target.closest?.('[data-cart-open]');
        if (opener && drawer()) {
          event.preventDefault();
          open();
          refresh().catch(() => {});
          return;
        }
        const closer = event.target.closest?.('[data-cart-close]');
        if (closer && closer.closest('[data-cart-drawer]')) {
          if (closer.tagName === 'A' && closer.getAttribute('href')?.includes('#')) {
            close();
            return;
          }
          event.preventDefault();
          close();
          return;
        }
        const lineButton = event.target.closest?.('[data-line-change]');
        if (lineButton) {
          event.preventDefault();
          lineButton.disabled = true;
          one('[data-cart-panel]')?.classList.add('is-loading');
          try { await change(lineButton.dataset.lineKey, Number(lineButton.dataset.quantity)); }
          finally { one('[data-cart-panel]')?.classList.remove('is-loading'); }
          return;
        }
        const quick = event.target.closest?.('[data-addon-quick-add]');
        if (quick) {
          event.preventDefault();
          quick.disabled = true;
          quick.textContent = 'Adding…';
          try { await add([{ id: quick.dataset.variant, quantity: 1 }]); } catch { quick.disabled = false; }
        }
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        trapFocus(event);
      });
      updateCount();
    };

    return { initialize, add, open, close, refresh, setBusy };
  })();

  /* ---------- Bundles + add-ons (one shared state for every picker on the page) ---------- */
  const state = { bundle: 'pair', addons: {} };

  const getBundleOptions = () => queryAll('[data-bundle-choice]:not(:disabled)');
  const bundleOption = (id) => getBundleOptions().find((node) => node.dataset.bundleId === id);
  const summaryQuantity = () => {
    const input = one('[data-bundle-quantity]');
    return input ? Math.min(10, Math.max(1, Number.parseInt(input.value, 10) || 1)) : 1;
  };
  const addonInfo = () => {
    const seen = new Map();
    queryAll('[data-addon]').filter((input) => !input.closest('.product-form')).forEach((input) => {
      if (!seen.has(input.dataset.addon)) {
        seen.set(input.dataset.addon, { key: input.dataset.addon, variant: input.dataset.addonVariant, cents: Number(input.dataset.addonPriceCents) || 0, name: input.dataset.addonName });
      }
    });
    return [...seen.values()];
  };
  const selectedAddons = () => addonInfo().filter((addon) => state.addons[addon.key]);
  const addonsCents = () => selectedAddons().reduce((sum, addon) => sum + addon.cents, 0);
  const itemsFor = (option, quantity) => [{ id: option.dataset.bundleVariant, quantity }]
    .concat(selectedAddons().map((addon) => ({ id: addon.variant, quantity: 1 })));
  const permalink = (items) => `${rootPath()}cart/${items.map((item) => `${item.id}:${item.quantity}`).join(',')}`;

  const syncBundle = (id = state.bundle) => {
    const option = bundleOption(id) || bundleOption('pair') || getBundleOptions()[0];
    if (!option) return;
    state.bundle = option.dataset.bundleId;
    const name = option.dataset.bundleName;
    const label = option.dataset.bundleLabel;
    const price = Number(option.dataset.bundlePriceCents) || 0;
    const extras = addonsCents();
    const chosen = selectedAddons();
    const qty = summaryQuantity();

    queryAll('[data-bundle-choice]').forEach((node) => {
      const active = node.dataset.bundleId === state.bundle && !node.disabled;
      node.classList.toggle('is-selected', active);
      node.setAttribute('aria-checked', String(active));
    });
    queryAll('[data-bundle-item]').forEach((item) => item.classList.toggle('is-selected', item.dataset.bundleItem === state.bundle));
    queryAll('[data-addon]').filter((input) => !input.closest('.product-form')).forEach((input) => { input.checked = Boolean(state.addons[input.dataset.addon]); });

    queryAll('[data-selected-bundle]').forEach((node) => { node.textContent = `${name} (${label})`; });
    queryAll('[data-selected-per-lamp]').forEach((node) => { node.textContent = option.dataset.bundlePerLamp; });
    queryAll('[data-selected-addons-short]').forEach((node) => { node.textContent = chosen.length ? ` + ${chosen.length} add-on${chosen.length > 1 ? 's' : ''}` : ''; });
    queryAll('[data-selected-addons-note]').forEach((node) => {
      node.textContent = chosen.length ? `Includes ${chosen.map((addon) => addon.name).join(' + ')}` : 'Touch controls + included remote with every lamp';
    });

    queryAll('[data-add-to-cart]').forEach((button) => {
      if (button.getAttribute('aria-busy') === 'true') return;
      const q = button.dataset.quantitySource === 'summary' ? qty : 1;
      const labelNode = one('[data-add-to-cart-label]', button);
      if (!labelNode) return;
      const total = formatMoney(price * q + extras);
      labelNode.textContent = button.closest('.mobile-checkout') ? `Add to cart · ${total}` : `Add ${name} to cart · ${total}`;
    });
    queryAll('[data-add-bundle]').forEach((button) => {
      if (button.getAttribute('aria-busy') === 'true') return;
      const cardOption = bundleOption(button.dataset.addBundle);
      const labelNode = one('[data-add-bundle-label]', button);
      if (!cardOption || !labelNode) return;
      const own = Number(cardOption.dataset.bundlePriceCents) || 0;
      const isActive = button.dataset.addBundle === state.bundle;
      labelNode.textContent = `Add ${cardOption.dataset.bundleName} to cart · ${formatMoney(own + (isActive ? extras : 0))}`;
    });
    queryAll('[data-buy-now]').forEach((link) => {
      const q = link.dataset.quantitySource === 'summary' ? qty : 1;
      link.href = permalink(itemsFor(option, q));
    });
  };

  const initializeBundles = () => {
    document.addEventListener('click', async (event) => {
      const choice = event.target.closest?.('[data-bundle-choice]');
      if (choice && !choice.disabled) syncBundle(choice.dataset.bundleId);

      const minus = event.target.closest?.('[data-quantity-minus]');
      const plus = event.target.closest?.('[data-quantity-plus]');
      const input = one('[data-bundle-quantity]');
      if (input && (minus || plus)) {
        input.value = String(Math.min(10, Math.max(1, summaryQuantity() + (plus ? 1 : -1))));
        syncBundle();
      }

      const cardButton = event.target.closest?.('[data-card-checkout]');
      if (cardButton) {
        event.preventDefault();
        const option = bundleOption(state.bundle);
        if (!option) return;
        const q = cardButton.dataset.quantitySource === 'summary' ? summaryQuantity() : 1;
        const addons = Object.keys(state.addons || {}).filter((key) => state.addons[key]);
        Cart.setBusy(cardButton, true, 'Going to secure card checkout…');
        try {
          const res = await fetch('https://auvyro-pay.mangment7.workers.dev/create-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bundle: state.bundle, quantity: q, addons, landing: 'BILLING' }),
          });
          const data = await res.json();
          if (!res.ok || !data.approvalUrl) throw new Error('worker order failed');
          window.location.href = data.approvalUrl;
        } catch (error) {
          try {
            await Cart.add(itemsFor(option, q));
          } catch (cartError) {
            window.location.href = permalink(itemsFor(option, q));
            return;
          }
          window.location.href = `${rootPath()}checkout`;
        } finally {
          Cart.setBusy(cardButton, false);
        }
      }

      const addButton = event.target.closest?.('[data-add-to-cart], [data-add-bundle]');
      if (addButton) {
        event.preventDefault();
        if (addButton.dataset.addBundle) syncBundle(addButton.dataset.addBundle);
        const option = bundleOption(state.bundle);
        if (!option) return;
        const q = addButton.dataset.quantitySource === 'summary' ? summaryQuantity() : 1;
        Cart.setBusy(addButton, true);
        try {
          await Cart.add(itemsFor(option, q));
        } catch (error) {
          // Fallback: if the AJAX cart is unavailable, go straight to Shopify checkout.
          if (!one('[data-cart-drawer]')) window.location.href = permalink(itemsFor(option, q));
        } finally {
          Cart.setBusy(addButton, false);
          syncBundle();
        }
      }
    });
    document.addEventListener('change', (event) => {
      if (event.target.matches?.('[data-bundle-quantity]')) {
        event.target.value = String(summaryQuantity());
        syncBundle();
      }
      if (event.target.matches?.('[data-addon]') && !event.target.closest?.('[data-product-form], .product-form')) {
        state.addons[event.target.dataset.addon] = event.target.checked;
        syncBundle();
      }
    });
    document.addEventListener('keydown', (event) => {
      const choice = event.target.closest?.('[data-bundle-choice]');
      if (!choice || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const group = choice.closest('[data-bundle-selector]');
      const items = queryAll('[data-bundle-choice]:not(:disabled)', group);
      const position = items.indexOf(choice);
      if (position < 0 || items.length < 2) return;
      event.preventDefault();
      const delta = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
      const next = items[(position + delta + items.length) % items.length];
      next.focus();
      syncBundle(next.dataset.bundleId);
    });
    syncBundle();
  };

  /* ---------- Product page: AJAX add (with add-ons) into the drawer ---------- */
  const initializeProductForm = (root = document) => {
    queryAll('.product-page__details', root).forEach((details) => {
      if (details.dataset.initialized) return;
      details.dataset.initialized = 'true';
      const form = one('form.product-form', details);
      const select = one('[data-product-variant]', details);
      const quantity = one('[data-product-quantity]', details);
      const buyNow = one('[data-product-buy-now]', details);
      const price = one('[data-product-price]', details);
      const compare = one('[data-product-compare]', details);
      const addButton = one('[data-product-add]', details);
      const errorBox = one('[data-product-error]', details);
      const variantId = () => select?.selectedOptions[0]?.value || one('input[name="id"]', details)?.value;
      const qty = () => Math.max(1, Number.parseInt(quantity?.value || '1', 10) || 1);
      const extras = () => queryAll('[data-addon]:checked', details).map((input) => ({ id: input.dataset.addonVariant, quantity: 1 }));
      const sync = () => {
        const selected = select?.selectedOptions[0];
        if (quantity) quantity.value = String(qty());
        if (buyNow && variantId()) buyNow.href = permalink([{ id: variantId(), quantity: qty() }].concat(extras()));
        if (selected && price) price.textContent = selected.dataset.price;
        if (selected && compare) { compare.textContent = selected.dataset.compare || ''; compare.hidden = !selected.dataset.compare; }
        if (selected && addButton) { addButton.disabled = selected.dataset.available !== 'true'; addButton.textContent = addButton.disabled ? 'Sold out' : 'Add to cart'; }
      };
      select?.addEventListener('change', sync);
      quantity?.addEventListener('change', sync);
      queryAll('[data-addon]', details).forEach((input) => input.addEventListener('change', sync));
      form?.addEventListener('submit', async (event) => {
        if (!one('[data-cart-drawer]')) return; // native form post as fallback
        event.preventDefault();
        if (errorBox) errorBox.hidden = true;
        const original = addButton?.textContent;
        if (addButton) { addButton.disabled = true; addButton.textContent = 'Adding…'; }
        try {
          await Cart.add([{ id: variantId(), quantity: qty() }].concat(extras()));
        } catch (error) {
          if (errorBox) { errorBox.textContent = String(error.message || 'Could not add to cart.'); errorBox.hidden = false; }
        } finally {
          if (addButton) { addButton.disabled = false; addButton.textContent = original || 'Add to cart'; }
        }
      });
      sync();
    });
  };

  const initializeStudio = (root = document) => {
    queryAll('[data-studio]', root).forEach((studio) => {
      if (studio.dataset.initialized) return;
      studio.dataset.initialized = 'true';
      const swatches = queryAll('[data-studio-swatch]', studio);
      if (!swatches.length) return;
      const power = one('[data-studio-power]', studio);
      const cycle = one('[data-studio-cycle]', studio);
      const touch = one('[data-studio-touch]', studio);
      const count = one('[data-studio-count]', studio);
      const name = one('[data-studio-name]', studio);
      let active = 0;
      let powered = true;
      let timer = null;

      const stopCycle = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
        cycle?.classList.remove('is-active');
        cycle?.setAttribute('aria-pressed', 'false');
        if (cycle) cycle.textContent = 'Auto-cycle';
      };
      const draw = () => {
        const swatch = swatches[active];
        studio.style.setProperty('--studio-color', swatch.dataset.color);
        studio.classList.toggle('is-off', !powered);
        if (name) name.textContent = powered ? `${String(active + 1).padStart(2, '0')} · ${swatch.dataset.name}` : 'Lamp standby';
        if (count) count.textContent = powered ? `${active + 1} / 16` : 'OFF';
        power?.setAttribute('aria-pressed', String(powered));
        power?.setAttribute('aria-label', powered ? 'Turn lamp preview off' : 'Turn lamp preview on');
        const powerText = one('span', power);
        if (powerText) powerText.textContent = powered ? 'On' : 'Off';
        swatches.forEach((item, idx) => {
          item.classList.toggle('is-selected', powered && idx === active);
          item.setAttribute('aria-checked', String(powered && idx === active));
        });
      };
      swatches.forEach((swatch, index) => swatch.addEventListener('click', () => {
        stopCycle();
        active = index;
        powered = true;
        draw();
      }));
      touch?.addEventListener('click', () => {
        stopCycle();
        if (powered) active = (active + 1) % swatches.length;
        powered = true;
        draw();
      });
      power?.addEventListener('click', () => {
        powered = !powered;
        if (!powered) stopCycle();
        draw();
      });
      cycle?.addEventListener('click', () => {
        if (timer) {
          stopCycle();
        } else {
          powered = true;
          cycle.classList.add('is-active');
          cycle.setAttribute('aria-pressed', 'true');
          cycle.textContent = 'Pause cycle';
          timer = window.setInterval(() => {
            active = (active + 1) % swatches.length;
            draw();
          }, 2100);
        }
        draw();
      });
      draw();
    });
  };

  const initializeReviews = (root = document) => {
    queryAll('[data-review-filters]', root).forEach((filters) => {
      if (filters.dataset.initialized) return;
      filters.dataset.initialized = 'true';
      const grid = one('[data-review-grid]', filters.parentElement);
      queryAll('[data-review-filter]', filters).forEach((button) => {
        button.addEventListener('click', () => {
          const category = button.dataset.reviewFilter;
          queryAll('[data-review-filter]', filters).forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          queryAll('[data-review-category]', grid).forEach((review) => {
            review.hidden = category !== 'all' && review.dataset.reviewCategory !== category;
          });
        });
      });
    });
  };

  const initializeReveal = (root = document) => {
    const nodes = queryAll('[data-reveal]:not([data-reveal-initialized])', root);
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.classList.add('has-motion');
    const observer = new IntersectionObserver((entries, current) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        current.unobserve(entry.target);
      });
    }, { threshold: .07, rootMargin: '0px 0px -30px 0px' });
    nodes.forEach((node) => {
      node.dataset.revealInitialized = 'true';
      observer.observe(node);
    });
  };

  const initializeMobileCheckout = () => {
    const hero = one('.hero-section');
    const pricing = one('#pricing');
    const dock = one('[data-mobile-checkout]');
    if (!hero || !dock || dock.dataset.initialized) return;
    dock.dataset.initialized = 'true';
    const update = () => {
      document.body.classList.toggle('is-past-hero', hero.getBoundingClientRect().bottom < 25);
      const visible = document.body.classList.contains('is-past-hero') && !document.body.classList.contains('is-pricing-visible');
      dock.toggleAttribute('inert', !visible);
      dock.setAttribute('aria-hidden', String(!visible));
    };
    window.addEventListener('scroll', update, { passive: true });
    if (pricing && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        document.body.classList.toggle('is-pricing-visible', entries[0].isIntersecting);
        update();
      }, { threshold: 0.15 }).observe(pricing);
    }
    update();
  };

  const initSections = (root = document) => {
    initializeAnnouncement(root);
    initializeGalleries(root);
    initializeStudio(root);
    initializeReviews(root);
    initializeProductForm(root);
    initializeReveal(root);
    syncBundle();
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializeHeader();
    Cart.initialize();
    initializeBundles();
    initSections();
    initializeMobileCheckout();
    document.addEventListener('shopify:section:load', (event) => {
      initializeHeader();
      initSections(event.target);
      initializeMobileCheckout();
    });
  });
})();
(function () {
  var WORKER_URL = 'https://auvyro-pay.mangment7.workers.dev/create-order';
  var WORKER_BUNDLES = { 49241668649078: 'starter', 49241671696502: 'pair', 49241671729270: 'trio' };
  var WORKER_ADDONS = { 49245912662134: 'priority', 49245912629366: 'protection' };
  var running = false;
  async function workerCheckoutFromCart() {
    try {
      var cart = await (await fetch('/cart.js')).json();
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;
      if ((cart.total_discount || 0) > 0) return false;
      var bundle = null, qty = 0, addons = [];
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var b = WORKER_BUNDLES[item.variant_id];
        if (b) {
          if (bundle) return false;
          bundle = b; qty = item.quantity;
        } else {
          var a = WORKER_ADDONS[item.variant_id];
          if (!a || item.quantity !== 1 || addons.indexOf(a) !== -1) return false;
          addons.push(a);
        }
      }
      if (!bundle || qty < 1 || qty > 10) return false;
      var res = await fetch(WORKER_URL, {
        method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ bundle: bundle, quantity: qty, addons: addons, landing: 'BILLING' }),
      });
      var data = await res.json();
      if (!res.ok || !data.approvalUrl) return false;
      window.location.href = data.approvalUrl;
      return true;
    } catch (e) { return false; }
  }
      document.addEventListener('click', async function (event) {
        var cardLink = event.target.closest && event.target.closest('a.cart-drawer__checkout, a.cart-form__checkout');
    if (!cardLink) return;
    if (running) { event.preventDefault(); return; }
      event.preventDefault();
      running = true;
      var ok = await workerCheckoutFromCart();
      running = false;
    if (!ok) window.location.href = cardLink.href;
      });
    })();